import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    Typography,
    Switch,
    FormControlLabel,
    TextField,
    Button,
    IconButton,
    Checkbox,
    FormGroup,
    Box,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    SelectChangeEvent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    DragIndicator as DragIndicatorIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { useWebUxConfig } from "../../custom-hooks/useWebUxConfig";
import { useGetFullProject } from "../../custom-hooks/useGetFullProject";
import { useStore, makeid_alpa_numeric } from "../../helpers";
import {
    AVAILABLE_FEATURE_LINKS_BY_CARD,
} from "../../pages/dashboard/dashboard-components/v3-widgets/featureLinks";

interface AdditionalLink {
    title: string;
    href: string;
}

interface ConfigCenterFeature {
    key: string;
    roles: string[];
}

interface CardConfig {
    type: string;
    title: string;
    feature_links?: string[];
    features?: ConfigCenterFeature[];
    additional_links?: AdditionalLink[];
    enabled?: boolean;
}

interface WebUxConfigType {
    ux_version: string;
    banner?: {
        enabled: boolean;
        lg: string;
        md: string;
        sm: string;
    };
    cards: CardConfig[];
}

const DEFAULT_CONFIG: WebUxConfigType = {
    ux_version: "v1",
    cards: [],
};

// Single source of truth for voter-records feature links (must always include all options)
const VOTER_RECORDS_FEATURE_LINKS = [
    "family-wise-grouping",
    "c3",
    "sir-impact",
    "voter-seg-status",
];

// All available card types with their default titles
const ALL_AVAILABLE_CARDS: Record<string, { title: string; defaultFeatureLinks?: string[] }> = {
    "ground-pulse": {
        title: "Ground Pulse",
        defaultFeatureLinks: ["task-manager", "field-team-report", "survey-web-link"],
    },
    "service-requests": {
        title: "Digital Office",
        defaultFeatureLinks: ["view-all", "assigned-to-me", "add-new", "settings"],
    },
    "voter-records": {
        title: "Voter Records",
        defaultFeatureLinks: VOTER_RECORDS_FEATURE_LINKS,
    },
    "config-center": {
        title: "Config Center",
    },
    "events-calendar": {
        title: "Scheduler",
    },
    "voter-reachout": {
        title: "Citizen Reachout",
    },
    "cm-connect-mock-dashboard": {
        title: "CM Connect Dashboard",
    },
    "cm-connect-mock-beneficiaries": {
        title: "CM Connect Beneficiaries",
    },
};

export const WebUxConfigBuilder: React.FC = () => {
    const { webUxConfig, isLoading } = useWebUxConfig();
    const { data: fullProject, isLoading: isLoadingProject } = useGetFullProject();
    const rootStore = useStore();
    const { projectStore } = rootStore;
    const queryClient = useQueryClient();
    const { projectid } = useParams<{ projectid: string }>();
    const [config, setConfig] = useState<WebUxConfigType>(DEFAULT_CONFIG);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // Merge config cards with all available cards
        const configCards = (webUxConfig as WebUxConfigType)?.cards || [];
        const configCardTypes = new Set(configCards.map((card) => card.type));

        // Create a map of existing config cards by type
        const configCardsMap = new Map<string, CardConfig>();
        configCards.forEach((card) => {
            configCardsMap.set(card.type, {
                ...card,
                enabled: card.enabled !== undefined ? card.enabled : true,
            });
        });

        // Start with cards from config (preserve order)
        const mergedCards: CardConfig[] = configCards.map((card) => {
            const defaultCard = ALL_AVAILABLE_CARDS[card.type];
            return {
                ...card,
                title: card.title || defaultCard?.title || card.type,
                enabled: card.enabled !== undefined ? card.enabled : true,
            };
        });

        // Add any missing cards from ALL_AVAILABLE_CARDS (disabled by default)
        Object.keys(ALL_AVAILABLE_CARDS).forEach((cardType) => {
            if (!configCardsMap.has(cardType)) {
                const defaultCard = ALL_AVAILABLE_CARDS[cardType];
                const newCard: CardConfig = {
                    type: cardType,
                    title: defaultCard.title,
                    enabled: false,
                };

                // Add default feature links structure if available
                if (defaultCard.defaultFeatureLinks) {
                    newCard.feature_links = [];
                }

                mergedCards.push(newCard);
            }
        });

        // Migrate v2 to v3 (New UX) since v2 is no longer supported
        let uxVersion = (webUxConfig as WebUxConfigType)?.ux_version || "v1";
        if (uxVersion === "v2") {
            uxVersion = "v3";
        }

        const configWithDefaults = {
            ux_version: uxVersion,
            banner: (webUxConfig as WebUxConfigType)?.banner,
            cards: mergedCards,
        };

        setConfig(configWithDefaults as WebUxConfigType);
    }, [webUxConfig]);

    const handleCardTitleChange = (cardIndex: number, title: string) => {
        const updatedCards = [...config.cards];
        updatedCards[cardIndex].title = title;
        setConfig({ ...config, cards: updatedCards });
    };

    const handleCardEnabledToggle = (cardIndex: number) => {
        const updatedCards = [...config.cards];
        const card = updatedCards[cardIndex];
        const cardDefaults = ALL_AVAILABLE_CARDS[card.type];

        // If enabling a card that was disabled, ensure it has proper structure
        if (card.enabled === false) {
            // Initialize with defaults if needed
            if (!card.feature_links && cardDefaults?.defaultFeatureLinks) {
                card.feature_links = [];
            }
            if (!card.additional_links) {
                card.additional_links = [];
            }
        }

        card.enabled = !card.enabled;
        setConfig({ ...config, cards: updatedCards });
    };

    const handleFeatureLinkToggle = (cardIndex: number, featureLink: string) => {
        const updatedCards = [...config.cards];
        const card = updatedCards[cardIndex];

        if (!card.feature_links) {
            card.feature_links = [];
        }

        const linkIndex = card.feature_links.indexOf(featureLink);
        if (linkIndex > -1) {
            card.feature_links.splice(linkIndex, 1);
        } else {
            card.feature_links.push(featureLink);
        }

        setConfig({ ...config, cards: updatedCards });
    };

    const handleAddAdditionalLink = (cardIndex: number) => {
        const updatedCards = [...config.cards];
        if (!updatedCards[cardIndex].additional_links) {
            updatedCards[cardIndex].additional_links = [];
        }
        updatedCards[cardIndex].additional_links!.push({ title: "", href: "" });
        setConfig({ ...config, cards: updatedCards });
    };

    const handleRemoveAdditionalLink = (cardIndex: number, linkIndex: number) => {
        const updatedCards = [...config.cards];
        updatedCards[cardIndex].additional_links!.splice(linkIndex, 1);
        setConfig({ ...config, cards: updatedCards });
    };

    const handleAdditionalLinkChange = (
        cardIndex: number,
        linkIndex: number,
        field: "title" | "href",
        value: string
    ) => {
        const updatedCards = [...config.cards];
        updatedCards[cardIndex].additional_links![linkIndex][field] = value;
        setConfig({ ...config, cards: updatedCards });
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const updatedCards = Array.from(config.cards);
        const [reorderedCard] = updatedCards.splice(result.source.index, 1);
        updatedCards.splice(result.destination.index, 0, reorderedCard);

        setConfig({ ...config, cards: updatedCards });
    };

    const handleConfigCenterFeatureToggle = (cardIndex: number, featureKey: string) => {
        const updatedCards = [...config.cards];
        const card = updatedCards[cardIndex];

        if (!card.features) {
            card.features = [];
        }

        const featureIndex = card.features.findIndex((f) => f.key === featureKey);
        if (featureIndex > -1) {
            card.features.splice(featureIndex, 1);
        } else {
            card.features.push({ key: featureKey, roles: [] });
        }

        setConfig({ ...config, cards: updatedCards });
    };

    const handleConfigCenterRolesChange = (
        cardIndex: number,
        featureKey: string,
        roles: string[]
    ) => {
        const updatedCards = [...config.cards];
        const card = updatedCards[cardIndex];
        const feature = card.features?.find((f) => f.key === featureKey);

        if (feature) {
            feature.roles = roles;
        }

        setConfig({ ...config, cards: updatedCards });
    };

    const handleSave = async () => {
        if (!fullProject) {
            toast.error("Project data not loaded");
            return;
        }

        try {
            setSaving(true);

            // Clean up the config before saving
            // Only save cards that are enabled OR have some configuration
            const cleanedConfig = {
                ...config,
                cards: config.cards
                    .filter((card) => {
                        // Include card if it's enabled OR has any configuration
                        const hasConfig =
                            (card.feature_links && card.feature_links.length > 0) ||
                            (card.features && card.features.length > 0) ||
                            (card.additional_links && card.additional_links.length > 0);
                        return card.enabled !== false || hasConfig;
                    })
                    .map((card) => {
                        const cleanedCard: any = {
                            type: card.type,
                            title: card.title,
                        };

                        // Only include enabled if it's explicitly false
                        if (card.enabled === false) {
                            cleanedCard.enabled = false;
                        }

                        if (card.feature_links && card.feature_links.length > 0) {
                            cleanedCard.feature_links = card.feature_links;
                        }

                        if (card.features && card.features.length > 0) {
                            cleanedCard.features = card.features;
                        }

                        if (card.additional_links && card.additional_links.length > 0) {
                            cleanedCard.additional_links = card.additional_links.filter(
                                (link) => link.title && link.href
                            );
                        }

                        return cleanedCard;
                    }),
            };

            // Clone the fullProject object to avoid mutating the original
            const updatedValues = _.cloneDeep(fullProject);

            // Ensure properties exist
            if (!updatedValues["properties"]) {
                updatedValues["properties"] = {};
            }

            // Ensure web_ux_config property exists
            if (!updatedValues["properties"]["web_ux_config"]) {
                updatedValues["properties"]["web_ux_config"] = {
                    id: makeid_alpa_numeric(5),
                    type: "json",
                    value: {},
                };
            }

            // Update the web_ux_config value
            updatedValues["properties"]["web_ux_config"]["value"] = cleanedConfig;

            // Prepare payload
            const payload = {
                id: updatedValues["id"],
                properties: updatedValues["properties"],
            };

            // Save to project properties
            await projectStore.editProjectProperties(payload);

            // Invalidate react-query cache for getfullproject
            if (projectid) {
                await queryClient.invalidateQueries({
                    queryKey: ["full-project", projectid],
                });
            }

            toast.success("Web UX Config saved successfully!");
        } catch (error) {
            console.error("Error saving config:", error);
            toast.error("Failed to save Web UX Config");
        } finally {
            setSaving(false);
        }
    };

    if (isLoading || isLoadingProject) {
        return <Typography>Loading...</Typography>;
    }

    // Available feature links for different card types (voter-records uses constant so option always shows)
    const availableFeatureLinks: Record<string, string[]> = {
        "ground-pulse": ["task-manager", "field-team-report", "survey-web-link"],
        "service-requests": ["view-all", "assigned-to-me", "add-new", "settings"],
        "voter-records": VOTER_RECORDS_FEATURE_LINKS,
    };

    const normalizeCardType = (type: string): string =>
        (type || "").toLowerCase().replace(/\s+/g, "-").replace(/_/g, "-");

    // Available features for config-center
    const configCenterAvailableFeatures = [
        "manage-surveys",
        "manage-properties",
        "manage-web-features",
        "manage-web-ux-config",
        "manage-profile-questions",
        "manage-voter-segmentation-config",
        "manage-banners",
        "user-management",
        "orgchart",
        "invite-user",
    ];

    const availableRoles = ["root", "client"];

    const renderConfigCenterCard = (card: CardConfig, cardIndex: number) => {
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                    Features:
                </Typography>
                {configCenterAvailableFeatures.map((featureKey) => {
                    const feature = card.features?.find((f) => f.key === featureKey);
                    const isEnabled = !!feature;

                    return (
                        <Box key={featureKey} sx={{ mb: 2, pl: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isEnabled}
                                        onChange={() => handleConfigCenterFeatureToggle(cardIndex, featureKey)}
                                    />
                                }
                                label={featureKey}
                            />
                            {isEnabled && (
                                <FormControl sx={{ ml: 4, minWidth: 200 }} size="small">
                                    <InputLabel>Roles</InputLabel>
                                    <Select
                                        multiple
                                        value={feature?.roles || []}
                                        onChange={(e: SelectChangeEvent<string[]>) => {
                                            handleConfigCenterRolesChange(
                                                cardIndex,
                                                featureKey,
                                                e.target.value as string[]
                                            );
                                        }}
                                        input={<OutlinedInput label="Roles" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {(selected as string[]).map((value) => (
                                                    <Chip key={value} label={value} size="small" />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        {availableRoles.map((role) => (
                                            <MenuItem key={role} value={role}>
                                                {role}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderStandardCard = (card: CardConfig, cardIndex: number) => {
        const normalizedType = normalizeCardType(card.type || "");
        const availableLinks =
            availableFeatureLinks[card.type] ||
            availableFeatureLinks[normalizedType] ||
            (normalizedType === "voter-records" ? VOTER_RECORDS_FEATURE_LINKS : []);

        return (
            <Box sx={{ mt: 2 }}>
                {availableLinks.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                            Feature Links:
                        </Typography>
                        <FormGroup>
                            {availableLinks.map((link) => (
                                <FormControlLabel
                                    key={link}
                                    control={
                                        <Checkbox
                                            checked={card.feature_links?.includes(link) || false}
                                            onChange={() => handleFeatureLinkToggle(cardIndex, link)}
                                        />
                                    }
                                    label={link}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                )}

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                        Additional Links:
                    </Typography>
                    {card.additional_links?.map((link, linkIndex) => (
                        <Box
                            key={linkIndex}
                            sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}
                        >
                            <TextField
                                size="small"
                                label="Title"
                                value={link.title}
                                onChange={(e) =>
                                    handleAdditionalLinkChange(cardIndex, linkIndex, "title", e.target.value)
                                }
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                label="URL"
                                value={link.href}
                                onChange={(e) =>
                                    handleAdditionalLinkChange(cardIndex, linkIndex, "href", e.target.value)
                                }
                                sx={{ flex: 2 }}
                            />
                            <IconButton
                                color="error"
                                onClick={() => handleRemoveAdditionalLink(cardIndex, linkIndex)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddAdditionalLink(cardIndex)}
                        size="small"
                    >
                        Add Link
                    </Button>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
                Web UX Configuration
            </Typography>

            {/* UX Version Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        UX Version
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography
                            sx={{
                                fontWeight: config.ux_version === "v1" ? "bold" : "normal",
                                color: config.ux_version === "v1" ? "primary.main" : "text.secondary",
                            }}
                        >
                            Old UX (v1)
                        </Typography>
                        <Switch
                            checked={config.ux_version === "v3"}
                            onChange={(e) =>
                                setConfig({ ...config, ux_version: e.target.checked ? "v3" : "v1" })
                            }
                        />
                        <Typography
                            sx={{
                                fontWeight: config.ux_version === "v3" ? "bold" : "normal",
                                color: config.ux_version === "v3" ? "primary.main" : "text.secondary",
                            }}
                        >
                            New UX (v3)
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Cards Configuration */}
            <Typography variant="h6" sx={{ mb: 2 }}>
                Cards Configuration
            </Typography>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="cards">
                    {(provided) => (
                        <Box {...provided.droppableProps} ref={provided.innerRef}>
                            {config.cards.map((card, cardIndex) => (
                                <Draggable
                                    key={`card-${cardIndex}`}
                                    draggableId={`card-${cardIndex}`}
                                    index={cardIndex}
                                >
                                    {(provided, snapshot) => (
                                        <Accordion
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            sx={{
                                                mb: 2,
                                                ...(snapshot.isDragging && {
                                                    boxShadow: 3,
                                                    opacity: 0.8,
                                                }),
                                                ...(card.enabled === false && {
                                                    opacity: 0.6,
                                                    bgcolor: "action.disabledBackground",
                                                }),
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    "& .MuiAccordionSummary-content": {
                                                        alignItems: "center",
                                                    },
                                                }}
                                            >
                                                <Box
                                                    {...provided.dragHandleProps}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        mr: 2,
                                                        cursor: "grab",
                                                        "&:active": {
                                                            cursor: "grabbing",
                                                        },
                                                    }}
                                                >
                                                    <DragIndicatorIcon
                                                        sx={{ color: "text.secondary" }}
                                                    />
                                                </Box>
                                                <Typography variant="h6" sx={{ flex: 1 }}>
                                                    {card.title}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        alignItems: "center",
                                                        mr: 2,
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mr: 2 }}
                                                    >
                                                        Type: <strong>{card.type}</strong>
                                                    </Typography>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={card.enabled !== false}
                                                                onChange={() =>
                                                                    handleCardEnabledToggle(cardIndex)
                                                                }
                                                            />
                                                        }
                                                        label="Enabled"
                                                    />
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box sx={{ mb: 3 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Card Title"
                                                        value={card.title}
                                                        onChange={(e) =>
                                                            handleCardTitleChange(cardIndex, e.target.value)
                                                        }
                                                        size="small"
                                                    />
                                                </Box>
                                                {card.type === "config-center"
                                                    ? renderConfigCenterCard(card, cardIndex)
                                                    : renderStandardCard(card, cardIndex)}
                                            </AccordionDetails>
                                        </Accordion>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Save Button */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? "Saving..." : "Save Configuration"}
                </Button>
            </Box>
        </Box>
    );
};

export default WebUxConfigBuilder;

